import { type ReactElement } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/HourglassEmpty";
import RevokeIcon from "@mui/icons-material/RemoveCircle";
import RejectedIcon from "@mui/icons-material/ThumbDown";

import type {
  InterviewParticipant,
  InterviewParticipantStatus,
} from "@thalamos/aspire-common";
import { isInviteExpired } from "@thalamos/aspire-common";

/**
 * Extends the persisted participant status with a computed "expired" value
 * for display purposes. "expired" is never stored in the database — it is
 * derived from a pending invite whose last invited date is older than 7 days.
 */
export type ParticipantDisplayStatus = InterviewParticipantStatus | "expired";

/**
 * Returns the display status for a participant. A pending participant whose
 * invite was last sent more than 7 days ago is treated as "expired".
 * "pending-confirmed" participants (actively selected by the AMHP) never
 * expire.
 */
export function getDisplayStatus(
  participant: InterviewParticipant,
): ParticipantDisplayStatus {
  if (
    participant.status === "pending" &&
    isInviteExpired(participant.lastInvitedAt)
  ) {
    return "expired";
  }
  return participant.status;
}

export function getStatusIcon(status: ParticipantDisplayStatus): ReactElement {
  switch (status) {
    case "pending":
      return <PendingIcon fontSize="small" color="warning" />;
    case "accepted":
      return <CheckCircleIcon fontSize="small" color="success" />;
    case "rejected":
      return <RejectedIcon fontSize="small" color="error" />;
    case "confirmed":
      return <CheckCircleIcon fontSize="small" color="primary" />;
    case "revoked":
      return <RevokeIcon fontSize="small" color="disabled" />;
    case "pending-confirmed":
      return <PendingIcon fontSize="small" color="info" />;
    case "expired":
      return <PendingIcon fontSize="small" color="error" />;
  }
}

export function getStatusColor(
  status: ParticipantDisplayStatus,
): "default" | "success" | "error" | "primary" | "warning" | "info" {
  switch (status) {
    case "pending":
      return "warning";
    case "accepted":
      return "success";
    case "rejected":
      return "error";
    case "confirmed":
      return "primary";
    case "revoked":
      return "default";
    case "pending-confirmed":
      return "info";
    case "expired":
      return "default";
  }
}

export function getStatusTextColor(status: ParticipantDisplayStatus): string {
  switch (status) {
    case "pending":
      return "#0B122C";
    case "accepted":
    case "rejected":
    case "confirmed":
    case "pending-confirmed":
      return "#FFFFFF";
    case "expired":
    case "revoked":
      return "#0B122C";
  }
}

export function getStatusLabel(status: ParticipantDisplayStatus): string {
  switch (status) {
    case "pending":
      return "Contacted";
    case "accepted":
      return "Available";
    case "rejected":
      return "Not Available";
    case "confirmed":
      return "Confirmed";
    case "revoked":
      return "Contacted";
    case "pending-confirmed":
      return "Awaiting Confirmation";
    case "expired":
      return "Expired";
  }
}
